import { JSX, useState } from 'react';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { ChatReport } from '../context/ChatBotContextProvider';
import { downloadBase64Pdf } from '../../utils/download-pdf';
import { CapturedStamp, isReplayed } from './CapturedStamp';

/** The size, in the unit somebody reading it would use. */
function readableSize(bytes: number): string {
    if (bytes >= 1024 * 1024) {
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }
    return Math.max(1, Math.round(bytes / 1024)) + ' KB';
}

/**
 * A report the assistant has written, offered as a download.
 *
 * The file arrives whole, inside the `report` frame, and lives in this component's state and
 * nowhere else. **It is not saved anywhere**, in the browser or on either server, so a reload
 * loses it and the report has to be asked for again. That is deliberate for a first version: a
 * PDF full of customer data that outlives the conversation needs somewhere to be kept and a rule
 * about when it is deleted, and neither of those exists yet.
 *
 * Downloading is therefore the only thing that makes it permanent, which is why the button says
 * so plainly rather than offering to preview it first.
 */
export const ReportCard: (props: { report: ChatReport, capturedAt?: number }) => JSX.Element =
    ({ report, capturedAt }): JSX.Element => {
        const [saved, setSaved] = useState<boolean>(false);

        function save(): void {
            downloadBase64Pdf(report.pdf, report.filename);
            setSaved(true);
        }

        return (
            <div className="chat__report">
                <DescriptionOutlinedIcon className="report__icon" fontSize="small" />

                <span className="report__detail">
                    <span className="report__title">{ report.title }</span>
                    <span className="report__meta">
                        PDF &middot; { readableSize(report.bytes) }
                        { saved && ' · saved to your downloads' }
                    </span>
                </span>

                { /* HQ-11. The file itself is never stored - it is only ever in this frame -
                     so a replayed report can say a report was written and when, and cannot offer
                     a download. Saying so beats a button that produces an empty document. */ }
                { isReplayed(capturedAt)
                    ? <CapturedStamp capturedAt={ capturedAt as number } />
                    : (
                        <button type="button" className="report__save" onClick={ save }>
                            <FileDownloadOutlinedIcon fontSize="small" />
                            { saved ? 'Download again' : 'Download' }
                        </button>
                    ) }
            </div>
        );
    };
